import { useCreateSecret, useUpdateSecret, StudioSecret, secretsQueries } from '@/features/secrets'
import { toastSuccess } from '@/utils/toast'
import { FormikConfig } from 'formik'
import { getSlug } from '@/utils/slug'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SecretFormSchema } from '../SecretForm/SecretForm.hooks'
import { yup } from '@knots/shared-frontend'

export type SecretModalSchemaType = {
  name: string
  slug: unknown
  value: string
}

export const useForm = (
  secret: StudioSecret | undefined,
  projectId: number,
  close: () => void
): FormikConfig<SecretModalSchemaType> => {
  // TODO Make proper conditional form
  const updateForm = useUpdateForm(secret ?? { name: 'test', slug: 'test', value: 'test' }, projectId, close)
  const createForm = useCreateForm(projectId, close)

  return secret ? updateForm : createForm
}

export const useUpdateForm = (
  secret: StudioSecret,
  projectId: number,
  close: () => void
): FormikConfig<SecretModalSchemaType> => {
  const mutation = useUpdateSecret(projectId, secret.id!)
  const queryClient = useQueryClient()
  const initialValues = {
    ...secret,
    value: '',
  }

  const onSubmit = async (values: SecretModalSchemaType) => {
    const cleared: Partial<StudioSecret> = { ...values, slug: getSlug(values.name) }
    if (cleared.value?.trim() === '') delete cleared.value

    await mutation.mutateAsync(cleared, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(secretsQueries.list(projectId))
      },
    })
    close()
    toastSuccess('Successfully updated the secret')
  }

  return { initialValues, onSubmit }
}

export const useCreateForm = (projectId: number, close: () => void): FormikConfig<SecretModalSchemaType> => {
  const initialValues = {
    name: 'New secret',
    slug: 'NEW_SECRET',
    value: '',
  }

  const mutation = useCreateSecret(projectId)
  const queryClient = useQueryClient()
  const secretListQuery = useQuery(secretsQueries.list(projectId))
  const validate = async (values: SecretModalSchemaType) => {
    const errors: Record<string, string> = {}

    // Format validation
    await SecretFormSchema.validate(values).catch((e) => {
      if (yup.ValidationError.isError(e)) {
        if (e.path) errors[e.path] = e.message
      }
    })

    const existingSecret = secretListQuery.data?.find((v) => getSlug(values.name) === v.slug)
    if (existingSecret) errors.slug = 'Already in use, enter a different name'

    return errors
  }

  const onSubmit = async (values: SecretModalSchemaType) => {
    const cleared: StudioSecret = { ...values, slug: getSlug(values.name) }
    await mutation.mutateAsync(cleared, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(secretsQueries.list(projectId))
      },
    })
    close()
    toastSuccess('Successfully created the secret')
  }

  return { initialValues, onSubmit, validate }
}
